/* The tolerance version in force, and the inline editor that publishes the next one.
 *
 * **A version is immutable, so "edit" means "publish another".** The editor starts from the bounds in
 * force rather than a blank form — a retune is almost always one bound in an otherwise settled list,
 * and retyping the ones being kept is how a bound gets mistyped on the one screen where a mistype is
 * permanent. Every run from the publish onwards cites the new version; every run before it keeps
 * citing the old one.
 */

import { useId, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  Definition,
  FieldTolerance,
  ToleranceKind,
  ToleranceVersion,
} from '@/shared/api/generated/reconciliation';
import { Button } from '@/shared/ui/Button';
import { HelpLabel } from '@/shared/ui/InfoTip';

import { usePublishTolerances } from '../api/governance';
import {
  BOUND_RULES,
  nextVersion,
  TOLERANCE_KINDS,
  termProblemKey,
  termToField,
  type TermDraft,
} from '../model/authoring';
import { formatRatioAsPercent } from '../model/reconciliation';

/** A bound as the design prints it: nothing for an exact field, a percentage for a ratio, and the
 * number itself otherwise. A zero would claim a numeric tolerance of zero on what may be a string
 * column, so an exact field reads as a dash. */
function boundText(field: FieldTolerance): string {
  const bound = field.bound;
  if (field.kind === 'exact' || bound === null || bound === undefined) return '—';
  if (field.kind === 'relative') return `${formatRatioAsPercent(bound)}%`;
  return String(bound);
}

const BLANK_TERM: TermDraft = {
  leftColumn: '',
  rightColumn: '',
  kind: 'exact',
  bound: '',
};

function termsFrom(version: ToleranceVersion): TermDraft[] {
  return version.fields.map((field) => ({
    leftColumn: field.left_column,
    rightColumn: field.right_column,
    kind: field.kind,
    bound: field.bound === null || field.bound === undefined ? '' : String(field.bound),
  }));
}

export function TolerancePanel({
  definition,
  canPublish,
}: {
  readonly definition: Definition;
  readonly canPublish: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const version = definition.tolerance_version;

  if (version === null) {
    return (
      <section className="lq-card lq-rc__panel lq-rdf__panel">
        <HelpLabel
          subject={t('reconciliation.tolerance.term')}
          help={t('help.term.tolerance')}
        >
          <h3 className="lq-rc__panelTitle">{t('reconciliation.tolerance.heading')}</h3>
        </HelpLabel>
        <p className="lq-rc__panelBody">{t('reconciliation.tolerance.unknownBody')}</p>
      </section>
    );
  }

  return (
    <ToleranceVersionPanel
      definitionId={definition.id}
      version={version}
      canPublish={canPublish}
    />
  );
}

function ToleranceVersionPanel({
  definitionId,
  version,
  canPublish,
}: {
  readonly definitionId: string;
  readonly version: ToleranceVersion;
  readonly canPublish: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const publish = usePublishTolerances(definitionId);
  const [terms, setTerms] = useState<TermDraft[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [published, setPublished] = useState<number | null>(null);
  const errorId = useId();

  function update(index: number, change: Partial<TermDraft>): void {
    if (terms === null) return;
    setTerms(terms.map((term, at) => (at === index ? { ...term, ...change } : term)));
    setProblem(null);
  }

  function close(): void {
    setTerms(null);
    setProblem(null);
    publish.reset();
  }

  function save(): void {
    if (terms === null) return;
    /* The server refuses a version comparing no field — comparing none would make every row agree —
       so the editor says so first rather than sending a request it knows will fail. */
    if (terms.length === 0) {
      setProblem(t('reconciliation.tolerance.editor.atLeastOne'));
      return;
    }
    for (const term of terms) {
      const key = termProblemKey(term);
      if (key !== null) {
        setProblem(t(key));
        return;
      }
    }
    const next = nextVersion(version);
    publish.mutate(
      { nextVersion: next, tolerances: terms.map(termToField) },
      {
        onSuccess: () => {
          setPublished(next);
          close();
        },
      },
    );
  }

  return (
    <section className="lq-card lq-rc__panel lq-rdf__panel">
      <div className="lq-rdf__panelHead">
        <div className="lq-rdf__panelTitles">
          <HelpLabel
            subject={t('reconciliation.tolerance.term')}
            help={t('help.term.tolerance')}
          >
            <h3 className="lq-rc__panelTitle">{t('reconciliation.tolerance.heading')}</h3>
          </HelpLabel>
          <div className="lq-rc__panelSub">
            {t('reconciliation.tolerance.versionLine', { version: version.version })}
          </div>
        </div>
        {canPublish && terms === null ? (
          <Button
            size="compact"
            onClick={() => {
              setPublished(null);
              setTerms(version.fields.length === 0 ? [BLANK_TERM] : termsFrom(version));
            }}
          >
            {t('reconciliation.publish.open')}
          </Button>
        ) : null}
      </div>

      {version.fields.map((field) => (
        <div
          /* Keyed on the pair, which a version holds once: an array index would make React reuse a
             row across two versions of the same definition. */
          key={`${field.left_column}:${field.right_column}`}
          className="lq-rdf__tolRow"
          title={t('reconciliation.tolerance.published', {
            who: version.published_by,
            when: new Date(version.published_at).toLocaleDateString(),
          })}
        >
          <span className="lq-mono lq-rdf__tolPair">
            {t('reconciliation.tolerance.pair', {
              left: field.left_column,
              right: field.right_column,
            })}
          </span>
          <span className="lq-rdf__tolKind">
            {t(`reconciliation.kind.${field.kind}`)}
          </span>
          <span className="lq-mono lq-rdf__tolBound">{boundText(field)}</span>
        </div>
      ))}
      {version.fields.length === 0 ? (
        <p className="lq-rdf__quiet">{t('reconciliation.tolerance.none')}</p>
      ) : null}

      {published !== null && (
        <p className="lq-rdf__saved" role="status">
          {t('reconciliation.tolerance.editor.published', { version: published })}
        </p>
      )}

      {terms !== null && (
        <div className="lq-rdf__editor">
          <div className="lq-rdf__terms">
            {terms.map((term, index) => (
              <div
                // A draft row has no identity of its own until it is published; the index is the
                // only stable handle while somebody is typing into it.
                key={index}
                className="lq-rdf__term"
              >
                <input
                  className="lq-input lq-mono"
                  value={term.leftColumn}
                  placeholder={t('reconciliation.tolerance.editor.leftPlaceholder')}
                  aria-label={t('reconciliation.tolerance.editor.left')}
                  onChange={(event) => {
                    update(index, { leftColumn: event.target.value });
                  }}
                />
                <input
                  className="lq-input lq-mono"
                  value={term.rightColumn}
                  placeholder={t('reconciliation.tolerance.editor.rightPlaceholder')}
                  aria-label={t('reconciliation.tolerance.editor.right')}
                  onChange={(event) => {
                    update(index, { rightColumn: event.target.value });
                  }}
                />
                <select
                  className="lq-input"
                  value={term.kind}
                  aria-label={t('reconciliation.tolerance.editor.kind')}
                  onChange={(event) => {
                    const kind = event.target.value as ToleranceKind;
                    update(index, {
                      kind,
                      bound: BOUND_RULES[kind].required ? term.bound : '',
                    });
                  }}
                >
                  {TOLERANCE_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {t(`reconciliation.kind.${kind}`)}
                    </option>
                  ))}
                </select>
                <input
                  className="lq-input"
                  inputMode="decimal"
                  value={term.bound}
                  disabled={!BOUND_RULES[term.kind].required}
                  placeholder={
                    BOUND_RULES[term.kind].placeholder ||
                    t('reconciliation.tolerance.editor.boundPlaceholder')
                  }
                  aria-label={t('reconciliation.tolerance.editor.bound')}
                  onChange={(event) => {
                    update(index, { bound: event.target.value });
                  }}
                />
                <button
                  type="button"
                  className="lq-rdf__remove"
                  aria-label={t('reconciliation.tolerance.editor.remove')}
                  onClick={() => {
                    setTerms(terms.filter((_, at) => at !== index));
                    setProblem(null);
                  }}
                >
                  ×
                </button>
              </div>
            ))}
            <Button
              size="compact"
              className="lq-rdf__add"
              onClick={() => {
                setTerms([...terms, BLANK_TERM]);
              }}
            >
              {t('reconciliation.tolerance.editor.add')}
            </Button>
          </div>

          {problem !== null && (
            <p id={errorId} role="alert" className="lq-rdf__error">
              {problem}
            </p>
          )}
          {publish.isError && (
            <p role="alert" className="lq-rdf__error">
              {publish.error.message}
            </p>
          )}

          <div className="lq-rdf__actions">
            <Button size="compact" onClick={close}>
              {t('reconciliation.publish.cancel')}
            </Button>
            <Button
              size="compact"
              tone="primary"
              busy={publish.isPending}
              aria-describedby={problem === null ? undefined : errorId}
              onClick={save}
            >
              {t('reconciliation.tolerance.editor.publish')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
